import "@fontsource-variable/dm-sans/wght.css";
import "node_modules/vaul/style.css";
import { useCallback, useMemo, useRef } from "react";

import { useMediaSession } from "~/components/audio-player/hooks/useMediaSession";
import { usePlayerEngine } from "~/components/audio-player/hooks/usePlayerEngine";
import { usePlayerStore } from "~/hooks/usePlayerStore";
import { getCurrentEpisodePlaybackLayout } from "~/lib/activeEpisodePlayback";
import { getEpisodePlaybackProgress } from "~/lib/episode.utils";

function debounce<TArgs extends Array<unknown>>(fn: (...args: TArgs) => void, delay: number): (...args: TArgs) => void {
  let timeout: NodeJS.Timeout;
  return (...args: TArgs) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

export function MediaSessionController() {
  const lastBackendSyncRef = useRef(0);
  const { devo, updateCompletion } = usePlayerStore();
  const { duration, playing, position, setPosition, play, pause, playable, usingFallbackSequence } = usePlayerEngine();

  const debouncedUpdateCompletion = useMemo(
    () =>
      debounce((devoId: string, pos: number, dur: number) => {
        updateCompletion(devoId, pos, dur);
      }, 1000),
    [updateCompletion],
  );
  const setPositionWithSync = useCallback(
    (pos: number) => {
      setPosition(pos);

      if (devo && playable) {
        const playbackProgress = getEpisodePlaybackProgress({
          progress: pos,
          duration,
          playable,
          ...getCurrentEpisodePlaybackLayout(devo, usingFallbackSequence),
        });

        debouncedUpdateCompletion(devo.id, playbackProgress.mainProgressSeconds, playbackProgress.mainDuration);

        lastBackendSyncRef.current = Date.now();
      }
    },
    [devo, playable, duration, setPosition, debouncedUpdateCompletion, usingFallbackSequence],
  );

  useMediaSession({
    devo,
    playing,
    duration,
    progress: position,
    setPlaying: (shouldPlay) => (shouldPlay ? play() : pause()),
    setPosition: setPositionWithSync,
  });
  return null;
}
